import { useTranslation, Trans } from "next-i18next";
import parse from "html-react-parser";
import {
    JavaIcon,
    PythonIcon, NodejsIcon,
    NextJsIcon
} from "../../components/v2/icons";
import AgentIntegration from "../../components/v2/agent-integration";
import Accordion from "../../components/v2/accordion/accordion";
import ProductResources from "../../components/v2/product/product-resource";
import ProductHeroSection from "../../components/v2/common/product-hero";
import ProductTour from "../../components/v2/product/product-tour";
import "../../styles/scss/_faq-section.module.scss";
import ProductOverviewSection from "../../components/v2/product-overview-section";
import { OpsAI, Serverless, RUM } from "../../components/v2/icons-v2";
import FeaturesMonitoringV2 from "../../components/v2/tabs/features-monitoring-v2";
import ClientTabsTestimonials from "../../components/v2/mw-carousel/testimonial-with-tabs";
import CallToActionDocsLink from "../../components/v2/blog/call-to-action-docs-link";

export default function APMMonitoring() {
    const { t } = useTranslation("common");

    const featuresData = t('product.apm-monitoring.features.detailedFeatures', { returnObjects: true }) as any[]
    const features = Array.isArray(featuresData) ? featuresData : []

    const faqsData = t('product.apm-monitoring.faq.questions', { returnObjects: true }) as any[]
    const faqs = Array.isArray(faqsData) ? faqsData : []

    const resourceData = t('product.apm-monitoring.resources', { returnObjects: true }) as any[]
    const resource = Array.isArray(resourceData) ? resourceData : []

    const productTourData = t('product.apm-monitoring.productTour', { returnObjects: true }) as any

    const agentIntegrationData = t('product.apm-monitoring.agentIntegration', { returnObjects: true }) as any

    const agentDeployData = t('product.shared.apmAgentDeploy', { returnObjects: true }) as any[]
    const agentDeployArray = Array.isArray(agentDeployData) ? agentDeployData : []

    const agentDeploy = agentDeployArray.map(agent => ({
        ...agent,
        icon: agent.key === 'java' ? <JavaIcon/> :
              agent.key === 'nodejs' ? <NodejsIcon/> :
              agent.key === 'nextjs' ? <NextJsIcon/> :
              agent.key === 'python' ? <PythonIcon/> :
              <span className={"text-gradient"}>20+</span>
    }))


    const productOverviewData = {
            title: "Product overview",
            buttons: [
                {
                    label: "View Docs",
                    link: "https://docs.middleware.io/apm-configuration/apm_overview",
                    className: "ahref-btn primary",
                },
                {
                    label: "View Pricing",
                    link: "/pricing/",
                    className: "ahref-btn secondary",
                },
            ],
            description:
                "Modern applications are spread across dozens of services, databases, and cloud environments. When something breaks, a dashboard alone won't help — you need full context. Middleware APM gives engineering teams distributed tracing, real-time service health metrics, and end-to-end root-cause analysis, all in one place. Logs, traces, metrics, and frontend sessions are correlated together, so you can go from alert to resolution without jumping between tools. It's built on OpenTelemetry with usage-based pricing — deep observability without the cost overhead of legacy platforms.",
            worksWellWithTitle: "Works Well With",
            worksWellWith: [
                {
                    title: "OpsAI",
                    link: "/product/ops-ai/",
                    icon: <OpsAI />
                },
                {
                    title: "Serverless",
                    link: "/product/serverless-monitoring/",
                    icon: <Serverless />
                },
                {
                    title: "RUM",
                    link: "/product/real-user-monitoring/",
                    icon: <RUM />
                },
            ],
        };

    return (
        <>

            <ProductHeroSection title={<h1>{t('product.apm-monitoring.hero.title')} <span>{t('product.apm-monitoring.hero.titleHighlight')}</span></h1>}
                                description={t('product.apm-monitoring.hero.description')}
                                img={"/images/v2/products/APM.webp"} imgMobile={"/images/v2/products/APM-mobile.webp"} imgAlt={t('product.apm-monitoring.hero.imageAlt')} preloadImg hasActions={true} className={"apm-monitoring"} tips={t('product.hero.tips')}/>


            <ProductOverviewSection productOverviewData={productOverviewData} />
            
            <FeaturesMonitoringV2 productFeatures={features} tourId={"apm-tour"} />

            <CallToActionDocsLink
                title={'Detect and fix application performance issues faster'}
                docsLink="https://docs.middleware.io/apm-configuration/apm_overview"
            />

            <section className="testimonial-section">
                <div className="container">
                    <div className="section-tag testimonial">{t("testimonials.tag")} 👤</div>
                    <h2>Hear it from the best: Why <span className="text-gradient">top companies</span> trust Middleware</h2>

                    <ClientTabsTestimonials />
                </div>
            </section>

            <ProductTour tourSrc={"https://app.supademo.com/embed/clzgum389153g9x772q7wawcv?embed_v=2"} title={productTourData.title} tourId={"apm-tour"} description={productTourData.description} titleChanged={<h2>Take an <span className="text-gradient">interactive tour </span> for application performance monitoring platform</h2>}/>

            <AgentIntegration
                agentDeploy={agentDeploy}
                title={
                    <h2>
                        <Trans
                            i18nKey="product.apm-monitoring.agentIntegration.title"
                            components={{ highlight: <span className="text-gradient" /> }}
                        />
                    </h2>
                }
                description={"Instrument your applications across Java, Python, Node.js, Go, .NET, PHP, and Ruby, with no complex setup required. Get real-time visibility into app performance, dependencies, and errors."}
                contactUsVariant="apm-monitoring"
            />

            <ProductResources resources={resource} title={t('product.shared.resources.title')}/>

            <section className={"faq-section"}>
                <div className={"container"}>
                    <h2>{t('product.apm-monitoring.faq.title')}</h2>
                    <p>{t('product.apm-monitoring.faq.subtitle')}</p>
                    <div className={"faq-item"}>
                        <Accordion faqs={faqs.map(faq => ({
                            ...faq,
                            answerPlain: faq.text,
                            text:
                                faq.textHtml != null && faq.textHtml !== ""
                                    ? parse(faq.textHtml)
                                    : <p>{faq.text}</p>
                        }))} showGroups={true}/>
                    </div>
                </div>
            </section>
        </>
    )
}